<template>
  <div class="mt-3">
    <div v-for="defect in pole.defects">
      <div :key="`container${defect._id}${defect.ilsas_verify}${defect.sesb_verify}${defect.inMaintenance}`">
        <div :id="`sheet_${defect._id}`" class="report sheet border rounded" :key="defect._id">
          <article :class="{ transparent: isLoading }">
            <div class="sheet-header">
              <div class="header-logo">
                <img :src="district.custom_logo.url"
                  v-if="district && district.custom_logo && district.custom_logo.url" />
                <img src="@/assets/images/tnb-logo-report.png" v-else />
                <div class="header-title border-top">
                  {{ filters.type }} Report
                </div>
              </div>
              <div class="header-info border-left">
                <div class="info-table">
                  <div class="table-row border-bottom">
                    <div class="table-cell table-head border-right">
                      <p>FEEDER</p>
                    </div>
                    <div class="table-cell table-cell-long table-head border-right">
                      <p class="pl-1 text-uppercase">{{ feeder }}</p>
                    </div>
                  </div>
                  <div class="table-row border-bottom">
                    <div class="table-cell table-subhead border-right">
                      <p>POLE NO</p>
                    </div>
                    <div class="table-cell border-right">
                      <p>{{ pole.name }}</p>
                    </div>
                    <div class="table-cell table-subhead border-right">
                      <p>DATE</p>
                    </div>
                    <div class="table-cell border-right">
                      <p>{{ dateFormat("date", pole.datetime) }}</p>
                    </div>
                    <div class="table-cell table-subhead border-right">
                      <p>TIME</p>
                    </div>
                    <div class="table-cell">
                      <p>{{ dateFormat("time", pole.datetime) }}</p>
                    </div>
                  </div>
                  <div class="table-row border-bottom">
                    <div class="table-cell table-subhead border-right">
                      <p>POLE TYPE</p>
                    </div>
                    <div class="table-cell border-right">
                      <p>{{ pole.type }} {{ pole.structure_type }}</p>
                    </div>
                    <div class="table-cell table-subhead border-right">
                      <p>VOLTAGE</p>
                    </div>
                    <div class="table-cell border-right">
                      <p>{{ `${voltage} V` }}</p>
                    </div>
                    <div class="table-cell table-subhead border-right">
                      <p>FL</p>
                    </div>
                    <div class="table-cell">
                      <p>{{ pole.fl_substation || 'Unknown' }}</p>
                    </div>
                  </div>
                  <div class="table-row border-bottom">
                    <div class="table-cell table-subhead border-right">
                      <p>LOAD</p>
                    </div>
                    <div class="table-cell border-right">
                      <p>{{ load ? `${load} A` : "Unknown" }}</p>
                    </div>
                    <div class="table-cell table-subhead border-right">
                      <p>PHASE</p>
                    </div>
                    <div class="table-cell border-right">
                      <p>{{ defect.phase || "Unknown" }}</p>
                    </div>
                    <div class="table-cell table-subhead border-right">
                      <p>STREET NAME</p>
                    </div>
                    <div class="table-cell">
                      <p>{{ pole.street_name || 'Unknown' }}</p>
                    </div>
                  </div>
                  <div class="table-row border-bottom">
                    <div class="table-cell table-subhead border-right">
                      <p>COORDINATE</p>
                    </div>
                    <div class="table-cell table-cell-long">
                      <p class="px-2">
                        {{ pole.lat ? pole.lat.toFixed(5) : "n/a" }},
                        {{ pole.lng ? pole.lng.toFixed(5) : "n/a" }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="sheet-components sheet-components-lv"
              :class="{ 'sheet-components-visual': defect.type == 'visual' }">
              <div class="component-table">
                <div class="table-row table-row-head border-bottom">
                  <div class="table-cell table-head text-center">
                    <p>COMPONENT / EQUIPMENT</p>
                  </div>
                </div>
                <div class="table-row border-bottom table-row-four">
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType("earthing", defect.component)
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p>EARTHING</p>
                  </div>
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType(
                          "phase_conductor",
                          defect.component
                        )
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p>PHASE CONDUCTOR</p>
                  </div>
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType(
                          "insulated_pearcing_connector_(ipc)",
                          defect.component
                        )
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p style="font-size: 0.85em;">INSULATED PEARCING CONNECTOR (IPC)</p>
                  </div>
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType(
                          "pole",
                          defect.component
                        )
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p>POLE</p>
                  </div>
                </div>
                <div class="table-row border-bottom table-row-four">
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType(
                          "black_box",
                          defect.component
                        )
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p>BLACK BOX</p>
                  </div>
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType(
                          "jumper",
                          defect.component
                        )
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p>JUMPER</p>
                  </div>
                  <div class="table-cell border-right">
                    <p class="position-relative text-center font-weight-bold">
                      {{
                        getComponentType(
                          "cable_termination",
                          defect.component
                        )
                      }}
                    </p>
                  </div>
                  <div class="table-cell table-subhead">
                    <p>CABLE TERMINATION</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="sheet-images">
              <div class="images-table">
                <div class="table-row border-bottom">
                  <div class="table-cell table-head w-100">
                    <p class="text-center">DEFECT IMAGE</p>
                  </div>
                </div>
                <div class="table-row row-images border-bottom">
                  <div class="table-cell border-right text-center p-2">
                    <div class="embed-responsive embed-responsive-4by3">
                      <div class="embed-responsive-item text-center">
                        <img class="clickable" :src="getImageUrl(defect._id, defect.type, 0)" @click="
                          toggleModal(
                            'galleryViewer',
                            true,
                            defect._id,
                            defect.type,
                            0,
                            defect.recommendation
                          )
                          " v-if="getImageUrl(defect._id, defect.type, 0)" />
                      </div>
                    </div>
                  </div>
                  <div class="table-cell text-center p-2">
                    <div class="embed-responsive embed-responsive-4by3">
                      <div class="embed-responsive-item text-center">
                        <img class="clickable" :src="getImageUrl(defect._id, defect.type, 1)" @click="
                          toggleModal(
                            'galleryViewer',
                            true,
                            defect._id,
                            defect.type,
                            1,
                            defect.recommendation
                          )
                          " v-if="getImageUrl(defect._id, defect.type, 1)" />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="table-row row-images border-bottom">
                  <div class="table-cell border-right text-center p-2">
                    <div class="embed-responsive embed-responsive-4by3">
                      <div class="embed-responsive-item text-center">
                        <img class="clickable" :src="getImageUrl(defect._id, defect.type, 2)" @click="
                          toggleModal(
                            'galleryViewer',
                            true,
                            defect._id,
                            defect.type,
                            2,
                            defect.recommendation
                          )
                          " v-if="getImageUrl(defect._id, defect.type, 2)" />
                      </div>
                    </div>
                  </div>
                  <div class="table-cell text-center p-2">
                    <div class="embed-responsive embed-responsive-4by3">
                      <div class="embed-responsive-item text-center">
                        <img class="clickable" :src="getImageUrl(defect._id, defect.type, 3)" @click="
                          toggleModal(
                            'galleryViewer',
                            true,
                            defect._id,
                            defect.type,
                            3,
                            defect.recommendation
                          )
                          " v-if="getImageUrl(defect._id, defect.type, 3)" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="sheet-measurements border-bottom border-top">
              <div class="measurements-table">
                <div class="table-row border-bottom">
                  <div class="table-cell w-100 text-center">
                    <p class="font-weight-bolder">
                      MEASUREMENTS
                    </p>
                  </div>
                </div>
                <div class="table-row row-measurements">
                  <div class="table-cell w-100 p-1">
                    <table class="table table-bordered table-sm mb-0 text-center">
                      <tr>
                        <th v-if="defect.bx1">Bx1</th>
                        <th v-if="defect.bx2">Bx2</th>
                        <th v-if="defect.sp1">Sp1</th>
                        <th v-if="defect.sp2">Sp2</th>
                        <th v-if="defect.deltaT">Dt1</th>
                        <th v-if="pole.ambient || temperature">Ambient</th>
                        <th v-if="pole.humidity || humidity">Humidity</th>
                      </tr>
                      <tr>
                        <td v-if="defect.bx1">{{ defect.bx1 }}</td>
                        <td v-if="defect.bx2">{{ defect.bx2 }}</td>
                        <td v-if="defect.sp1">{{ defect.sp1 }}</td>
                        <td v-if="defect.sp2">{{ defect.sp2 }}</td>
                        <td v-if="defect.deltaT">{{ defect.deltaT }}</td>
                        <td v-if="pole.ambient || temperature">{{ pole.ambient || temperature }} °C</td>
                        <td v-if="pole.humidity || humidity">{{ pole.humidity || humidity }} %</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="sheet-remarks">
              <div class="remarks-table">
                <div class="table-row border-bottom">
                  <div class="table-cell w-100 text-center">
                    <p class="font-weight-bolder">
                      {{ defect.type.toUpperCase() }} DEFECT
                    </p>
                  </div>
                </div>
                <div class="table-row row-remarks border-bottom">
                  <div class="table-cell table-cell-long w-100">
                    <p style="font-size: 1.1em;">{{ defect.remark || "No remarks" }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="sheet-recommendations">
              <div class="recommendations-table">
                <div class="table-row border-bottom">
                  <div class="table-cell w-100 text-center">
                    <p class="font-weight-bolder">
                      ANALYSIS &amp; RECOMMENDATION
                    </p>
                  </div>
                </div>
                <div class="table-row row-recommendations" :class="getRowClass(defect.severity || defect.status)">
                  <div class="table-cell w-100">
                    <p style="font-size: 1.1em;">{{ defect.recommendation || "None" }}</p>
                  </div>
                </div>
              </div>
            </div>
          </article>
          <loading-spinner class="loading-spinner-component" v-if="isLoading"></loading-spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import API from "@/api.js";
import moment from "moment";
import Swal from "sweetalert2";
import LoadingSpinner from "@/components/LoadingSpinner";

export default {
  name: "reportSheetDefectLV",
  props: [
    "district",
    "source",
    "feeder",
    "category",
    "pole",
    "poleModel",
    "filters",
    "sheetType",
    "voltage",
    "load",
    "temperature",
    "humidity",
    "redisRoute",
  ],
  components: {
    "loading-spinner": LoadingSpinner,
  },
  data() {
    return {
      isLoading: false,
      defectImages: [],
      user: null,
      verifiers: ["ilsas", "sesb"],
      visualImages: [],
      thermalImages: [],
    };
  },
  watch: {
    "pole.defects": {
      async handler() {
        if (this.pole.defects.length > 0) {
          this.resizeSheet();
          await this.fetchDefectImages();
        }
      },
      immediate: true,
    },
  },
  methods: {
    addToMaintenance(defect) {
      Swal.fire({
        title: "<small>Are you sure?</small>",
        html: `<small>${defect.inMaintenance ? "Remove from" : "Add to"
          } maintenance list</small`,
        icon: "question",
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        showCancelButton: true,
        buttonsStyling: false,
        reverseButtons: true,
        customClass: {
          confirmButton: "btn btn-primary btn-sm px-4",
          cancelButton: "btn btn-link btn-sm px-4",
          actions: "border-top pt-3 d-flex justify-content-between",
        },
        showLoaderOnConfirm: true,
        preConfirm: (value) => {
          this.$emit("isLoading", true);

          let body = {
            recache: true,
          };

          if (this.poleModel) {
            body["poleId"] = this.poleModel;
          }

          if (this.redisRoute) {
            body["redisRoute"] = "/".concat(this.redisRoute);
          }

          body.inMaintenance = !defect.inMaintenance;

          return API.put(`defects/addToMaintenance/${defect._id}`, body)
            .then((retVal) => {
              return retVal.data;
            })
            .catch((err) => {
              this.$emit("isLoading", false);
              console.error(err);
              Swal.showValidationMessage(`Request failed: ${err}`);
            });
        },
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.value) {
          Swal.fire(
            "",
            `Defect ${defect.inMaintenance ? "removed from" : "added to"
            } maintenance`,
            "success"
          );
          this.$emit("defectVerified", result.value);
        }
      });
    },
    viewRejectReason(v) {
      Swal.fire({
        title: "<small class='text-danger'>Reason of Rejection</small>",
        html: `<small>${v.split("||")[1]}</small>`,
        confirmButtonText: "Close",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-link btn-sm px-4",
        },
      });
    },
    getDefectVerificationStatus(v) {
      if (v) {
        let vArray = v.split(":");
        return vArray[0] == "true" ? "true" : "rejected";
      } else {
        return false;
      }
    },
    verifyDefect(type, defect) {
      if (type == "approve") {
        Swal.fire({
          title: "<small class='text-success'>Confirm Defect</small>",
          html: `<small>Do you want to confirm this defect<br/><span class="font-weight-bold">"${defect.remark}"</span>?</small>`,
          icon: "question",
          confirmButtonText: "Confirm",
          cancelButtonText: "Cancel",
          showCancelButton: true,
          buttonsStyling: false,
          reverseButtons: true,
          customClass: {
            confirmButton: "btn btn-success btn-sm px-4",
            cancelButton: "btn btn-link btn-sm px-4",
            actions: "border-top pt-3 d-flex justify-content-between",
          },
        }).then((result) => {
          if (result.value) {
            if (
              this.user &&
              this.user.company &&
              this.verifiers.includes(this.user.company.toLowerCase())
            ) {
              this.$emit("isLoading", true);

              let body = {
                recache: true,
              };

              if (this.poleModel) {
                body["poleId"] = this.poleModel;
              }

              if (this.redisRoute) {
                body["redisRoute"] = "/".concat(this.redisRoute);
              }

              body[`${this.user.company.toLowerCase()}_verify`] = "true";

              API.put(`defects/${defect._id}`, body)
                .then((retVal) => {
                  Swal.fire("Approved!", "This defect is verified", "success");
                  this.$emit("defectVerified", retVal.data);
                })
                .catch((err) => {
                  this.$emit("isLoading", false);
                  console.error(err);
                });
            }
          }
        });
      } else if (type == "reject") {
        Swal.fire({
          title: "<small class='text-danger'>Reject Defect</small>",
          html: `<small>Do you want to reject this defect<br/><span class="font-weight-bold">"${defect.remark}"</span>?</small>`,
          icon: "question",
          confirmButtonText: "Reject",
          cancelButtonText: "Cancel",
          showCancelButton: true,
          buttonsStyling: false,
          reverseButtons: true,
          customClass: {
            confirmButton: "btn btn-danger btn-sm px-4",
            cancelButton: "btn btn-link btn-sm px-4",
            actions: "border-top pt-3 d-flex justify-content-between",
          },
        }).then((result) => {
          if (result.value) {
            if (
              this.user &&
              this.user.company &&
              this.verifiers.includes(this.user.company.toLowerCase())
            ) {
              Swal.fire({
                title: "<small>Reason of rejection</small>",
                html: "<small>Please state reason of rejection</small",
                icon: "question",
                confirmButtonText: "Reject",
                cancelButtonText: "Cancel",
                showCancelButton: true,
                buttonsStyling: false,
                reverseButtons: true,
                customClass: {
                  confirmButton: "btn btn-danger btn-sm px-4",
                  cancelButton: "btn btn-link btn-sm px-4",
                  actions: "border-top pt-3 d-flex justify-content-between",
                },
                input: "textarea",
                inputAttributes: {
                  autocapitalize: "off",
                },
                showLoaderOnConfirm: true,
                preConfirm: (value) => {
                  this.$emit("isLoading", true);

                  let body = {
                    recache: true,
                  };

                  if (this.poleModel) {
                    body["poleId"] = this.poleModel;
                  }

                  if (this.redisRoute) {
                    body["redisRoute"] = "/".concat(this.redisRoute);
                  }

                  body[
                    `${this.user.company.toLowerCase()}_verify`
                  ] = `false||${value}`;
                  return API.put(`defects/${defect._id}`, body)
                    .then((retVal) => {
                      return retVal.data;
                    })
                    .catch((err) => {
                      this.$emit("isLoading", false);
                      console.error(err);
                      Swal.showValidationMessage(`Request failed: ${error}`);
                    });
                },
                allowOutsideClick: () => !Swal.isLoading(),
              }).then((result) => {
                if (result.value) {
                  Swal.fire("Rejected!", "This defect is rejected", "success");
                  this.$emit("defectVerified", result.value);
                }
              });
            }
          }
        });
      }
    },
    toggleModal(modalName, show, defectId, defectType, index, caption) {
      if (show) {
        if (
          modalName === "galleryViewer" &&
          this.getImageUrl(defectId, defectType, index)
        ) {
          let galleryImageIndex = index;
          let galleryImages = [
            this.getImageUrl(defectId, defectType, 0),
            this.getImageUrl(defectId, defectType, 1),
            this.getImageUrl(defectId, defectType, 2),
            this.getImageUrl(defectId, defectType, 3),
          ];

          this.$emit(
            "toggleModal",
            modalName,
            show,
            galleryImages,
            galleryImageIndex,
            caption
          );
        }
      }
    },
    getImageUrl(defectId, defectType, index) {
      if (
        this.defectImages.filter((di) => di._id == defectId).length &&
        this.defectImages.filter((di) => di._id == defectId)[0].defectImages
          .length > 0 &&
        index >= 0 &&
        index <
        this.defectImages.filter((di) => di._id == defectId)[0].defectImages
          .length
      ) {
        let imageUrl = this[`${defectType}Images`].filter(
          (i) => i.defectId == defectId
        )[index];

        if (imageUrl) {
          return imageUrl.url && imageUrl.url.substring(0, 4) == "http"
            ? imageUrl.url
            : "https://".concat(imageUrl.url);
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    async fetchDefectImages() {
      this.defectImages = this.pole.defects;

      this.visualImages = this.pole.defects
        .filter((d) => d.type == "visual")
        .map((d) => {
          let x = d.defectImages.map((i) => {
            i["defectId"] = d._id;
            return i;
          });
          return x;
        })
        .flat();

      this.thermalImages = this.pole.defects
        .filter((d) => d.type == "thermal")
        .map((d) => {
          let x = d.defectImages.map((i) => {
            i["defectId"] = d._id;
            return i;
          });
          return x;
        })
        .flat();

      this.visualImages.length > 0 &&
        this.visualImages.sort((a, b) => {
          let aName = a.name ? a.name.toLowerCase() : null;
          let bName = b.name ? b.name.toLowerCase() : null;

          return aName < bName ? -1 : 1;
        });

      this.visualImages.length > 0 &&
        this.thermalImages.sort((a, b) => {
          let aName = a.name.toLowerCase();
          let bName = b.name.toLowerCase();

          return aName < bName ? -1 : 1;
        });
    },
    getRowClass(severity) {
      let result;

      switch (severity) {
        case "low":
          result = "bg-low";
          break;
        // case "minor":
        //   result = "bg-normal";
        //   break;
        case "cautious":
        case "minor":
          result = "bg-minor";
          break;
        case "critical":
          result = "bg-critical";
          break;
        default:
          result = "bg-light";
          break;
      }

      return result;
    },
    getComponentType(type, component, severity) {
      if (component) {
        if (typeof component === "object") {
          return component.includes(type) ? "X" : "";
        } else if (severity && severity.length > 1) {
          return (component == "minor" ? "cautious" : component) == type
            ? "X"
            : "";
        } else {
          return type
            .replace(/\//g, "_")
            .replace(/_/g, " ")
            .replace(/ /g, "")
            .toLowerCase() ==
            component.replace(/\//g, "").replace(/ /g, "").toLowerCase()
            ? "X"
            : "";
        }
      } else {
        return "";
      }
    },
    dateFormat(type, date) {
      if (date) {
        if (type === "date") {
          return moment.utc(date).format("DD/MM/YYYY");
        } else if (type === "time") {
          return moment.utc(date).format("h:mm A");
        } else {
          return date;
        }
      } else {
        return "";
      }
    },
    resizeSheet(skipTimeout) {
      this.isLoading = skipTimeout ? false : true;
      setTimeout(
        () => {
          for (let index = 0; index < this.pole.defects.length; index++) {
            const defect = this.pole.defects[index];
            let elementId = `#sheet_${defect._id}`;
            const sheet = $(elementId);
            const sheetWidth = sheet.width();
            sheet.height(sheetWidth * 1.2414285714);
            sheet.css({
              fontSize: sheetWidth * 0.01026119403,
            });
            const title = sheet
              .children()
              .children()
              .children()
              .children(".header-title");
            title.css({
              fontSize: sheetWidth * 0.01679104478,
            });
          }
          this.isLoading = false;
        },
        skipTimeout ? 10 : 2000
      );
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
    $(window).on("resize", (e) => {
      this.resizeSheet(true);
    });
  },
  beforeDestroy() {
    $(window).off("resize");
  },
};
</script>

<style lang="scss" scoped>
@import url("../assets/css/print.scss");

.bg-gray {
  background-color: #f5f7fa !important;
}

.sheet {
  width: 100% !important;
  height: auto;
  position: relative;

  article {
    width: 100%;
    height: 100%;
    display: block;

    &.transparent {
      opacity: 0;
    }

    .sheet-header {
      height: 10% !important;

      .header-logo {
        display: inline-block;
        vertical-align: top;
        width: 20%;
        height: 100%;
        position: relative;
        text-align: center;

        img {
          width: 95%;
          height: 73% !important;
          padding: 5px;
          object-fit: contain;
        }

        .header-title {
          height: 27% !important;
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          font-weight: 600;
          text-transform: uppercase;
        }
      }

      .header-info {
        display: inline-block;
        vertical-align: top;
        width: 80%;
        height: 100%;

        .info-table {
          width: 100%;
          height: 100%;

          .table-row {
            height: 20% !important;

            .table-cell {
              width: 16.666666667%;

              &.table-cell-long {
                width: 83.333333333%;
              }
            }
          }
        }
      }
    }

    .sheet-components {
      height: 8% !important;

      &.sheet-components-visual {
        height: 10% !important;

        .table-row {
          &.table-row-head {
            height: 25% !important;
          }
        }
      }

      &.sheet-components-lv {
        height: 5% !important;

        .table-row {
          &.table-row-thermal {
            height: 35% !important;

            .table-cell.table-subhead {
              width: 28.33%;
            }
          }

          &.table-row-four {
            height: 35% !important;

            .table-cell.table-subhead {
              width: 20%;
            }
          }
        }
      }

      .component-table {
        width: 100%;
        height: 100%;

        .table-row {
          height: 18.75%;

          &.table-row-head {
            height: 30%;
          }

          &.table-row-thermal {
            height: 23.333333333%;
          }

          .table-cell {
            width: 5%;

            &.table-head {
              width: 100%;
            }

            &.table-subhead {
              width: 20%;
            }
          }
        }

        .table-row-alt {
          height: 20% !important;

          &.table-row-thermal {
            height: 23.333333333% !important;
          }

          .table-cell {
            width: 3%;

            &.table-head {
              width: 100%;
            }

            &.table-subhead {
              width: 17%;
            }
          }
        }
      }
    }

    .sheet-images {
      height: 63.5% !important;

      .images-table {
        width: 100%;
        height: 100%;

        .table-row {
          height: 4%;

          &.row-images {
            height: fit-content;
          }

          .table-cell {
            width: 50%;
          }
        }
      }
    }

    .sheet-remarks {
      height: 7% !important;

      .remarks-table {
        width: 100%;
        height: 100%;

        .table-row {
          height: 25% !important;

          &.row-remarks {
            height: 75% !important;
          }

          .table-cell {
            width: 20%;

            &.table-cell-long {
              width: 80%;
            }

            .inner-row {
              width: 100%;
              height: 33.5% !important;
              position: relative;

              &:nth-child(odd) {
                height: 33.25% !important;
              }

              .inner-cell {
                position: relative;
                display: inline-block;
                vertical-align: top;
                height: 100%;
                width: 25%;

                p {
                  margin: 0;
                  position: absolute;
                  top: 50%;
                  left: 0;
                  transform: translate(10%, -50%);
                  line-height: 110%;
                  width: 100%;
                  padding: 0px 3%;
                }
              }

              .inner-cell-long {
                position: relative;
                display: inline-block;
                vertical-align: top;
                height: 100%;
                width: 75%;

                p {
                  margin: 0;
                  position: absolute;
                  top: 50%;
                  left: 0;
                  transform: translate(10%, -50%);
                  line-height: 110%;
                  width: 100%;
                  padding: 0px 3%;
                }
              }
            }
          }
        }
      }
    }

    .sheet-recommendations {
      height: 7.5% !important;

      .recommendations-table {
        width: 100%;
        height: 100%;

        .table-row {
          height: 25% !important;

          &.row-recommendations {
            height: 75% !important;
          }
        }
      }
    }

    .sheet-measurements {
      height: 7% !important;

      .measurements-table {
        width: 100%;
        height: 100%;

        .table-row {
          height: 30% !important;

          &.row-measurements {
            height: 70% !important;

            th {
              background-color: #bdd7ee !important;
            }
          }
        }
      }
    }

    .table-cell {
      position: relative;
      display: inline-block;
      vertical-align: top;
      padding: 0px 1%;
      height: 100%;

      p {
        margin: 0;
        position: absolute;
        top: 50%;
        left: 0;
        transform: translate(0, -50%);
        line-height: 110%;
        width: 100%;
        padding: 0px 3%;
      }

      img {
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        display: block;
        object-fit: contain;
      }
    }

    .table-head {
      color: #ffffff !important;
      background-color: #004c9c !important;
      font-weight: 500;
    }

    .table-subhead {
      background-color: #bdd7ee !important;
    }
  }
}
</style>
